<template>
  <teleport to="body">
    <div v-if="open" class="modal" @click.self.prevent="$emit('close')">
      <div class="content">
        <font-awesome-icon icon="fa-xmark" class="exit" @click="$emit('close')" />
        <h1>Cash Up</h1>
        <div class="container">
          <section class="column-container">
            <h5>Last 7 adjustments</h5>
            <div class="info" v-for="adj in recentAdjustments" :key="adj.id">
              <span class="blue">
                <CurrencyFormatter :value="adj.doc.value" :currency="currency" /> <span class="cap">{{ docType(adj.doc.doc_type, adj.doc.reason) }}</span> on
                {{ new Date(adj.doc.timestamp).toLocaleDateString() }} by {{ adj.doc.name }}
                <span v-if="isToday(adj.doc.timestamp)" class="today-pill">Today</span>
              </span>
            </div>
          </section>
          <section class="column-container">
            <div v-if="missedDays > 0" class="missed-warning">
              <font-awesome-icon icon="fa-triangle-exclamation" />
              Last cash up was on {{ formatDate(lastCashUpDate) }} ({{ missedDays }} day{{ missedDays > 1 ? 's' : '' }} ago). Payments shown include all days since then.
            </div>
            <h5>{{ missedDays > 0 ? 'Payments Since Last Cash Up' : 'Todays Payments' }}</h5>
            <ul class="row-container">
              <li v-for="(payment, idx) in paymentLabels" :key="idx">
                <div class="table">
                  <p>{{ payment }}:</p>
                  <CurrencyFormatter :value="paymentData[idx]" :currency="currency" />
                </div>
              </li>
              <li>
                <div class="table">
                  <p>Total:</p>
                  <CurrencyFormatter :value="todaysTotal" :currency="currency" />
                </div>
              </li>
            </ul>
            <div class="estimate">
              <p>Estimated Float amount: <CurrencyFormatter :value="estimatedFloatAmount" :currency="currency" /></p>
              <p v-if="pendingAdjustmentAt" class="today-adjustment">
                Estimate before adjustment:
                <CurrencyFormatter :value="baseEstimatedFloat" :currency="currency" />
              </p>
              <p v-if="pendingAdjustmentAt" class="today-adjustment">
                Adjustment:
                <span class="cap">{{ docType(pendingAdjustmentType, pendingAdjustmentReason) }}</span>
                <CurrencyFormatter :value="Math.abs(pendingAdjustment)" :currency="currency" />
                <span class="adjustment-date">{{ formatDate(pendingAdjustmentAt) }}</span>
              </p>
              <p v-else-if="hasAdjustmentToday" class="today-adjustment">An adjustment has already been recorded today.</p>
              <button class="adjust-float" @click.stop.prevent="showFloatModal = true">{{ floatAdjustments.length == 0 ? 'Create Float' : 'Adjust Float' }}</button>
              <FloatAdjustment :open="showFloatModal" :currentFloatAmount="estimatedFloatAmount" @close="showFloatModal = false" @amounts-changed="handleAmountsChanged" />
            </div>
            <div class="enter-name">
              <label for="name">Cash up completed by:</label>
              <input type="text" name="name" id="name" v-model="name" placeholder="Enter name..." />
            </div>
          </section>
        </div>
        <button class="complete" @click.prevent="completeCashUp">Complete cash up</button>
      </div>
    </div>
  </teleport>
</template>

<script>
import FloatAdjustment from "./FloatAdjustment.vue";
import { CurrencyFormatter } from "@monolith/components";
import { mapGetters } from "vuex";
export default {
  name: "CashUpModal",
  components: { CurrencyFormatter, FloatAdjustment },
  data() {
    return {
      showFloatModal: false,
      name: "",
      pendingAdjustment: 0,
      pendingAdjustmentType: null,
      pendingAdjustmentAt: null,
      pendingAdjustmentReason: null,
    };
  },
  props: ["open", "floatAmount", "floatAdjustments", "paymentLabels", "paymentData", "todaysTotal", "managerName", "missedDays", "lastCashUpDate"],
  emits: ["close", "cashUpCompleted"],
  watch: {
    managerName(val) {
      if (val) this.name = val;
    },
    open(val) {
      if (val && this.managerName) this.name = this.managerName;
    },
  },
  methods: {
    handleAmountsChanged(amounts) {
      if (!amounts || !amounts.amount || !amounts.type) return;
      const absAmount = Math.abs(Number(amounts.amount) || 0);
      this.pendingAdjustment = amounts.type === "debit" ? -absAmount : absAmount;
      this.pendingAdjustmentType = amounts.type;
      this.pendingAdjustmentAt = new Date().toISOString();
      this.pendingAdjustmentReason = amounts.reason || null;
    },

    completeCashUp() {
      this.$emit("cashUpCompleted", {
        name: this.name,
        cashSales: this.cashTotal,
        adjustmentAmount: Math.abs(this.pendingAdjustment),
        adjustmentType: this.pendingAdjustmentType,
        adjustmentReason: this.pendingAdjustmentReason,
      });
      this.name = "";
      this.pendingAdjustment = 0;
      this.pendingAdjustmentType = null;
      this.pendingAdjustmentAt = null;
      this.pendingAdjustmentReason = null;
    },

    isToday(timestamp) {
      return timestamp && new Date(timestamp).toDateString() === new Date().toDateString();
    },

    formatDate(timestamp) {
      if (!timestamp) return "";
      return new Date(timestamp).toLocaleDateString("en-GB");
    },

    docType(type, reason) {
      if (type === "credit") {
        return "Added";
      } else {
        return reason ? `Removed (${reason})` : "Removed";
      }
    },
  },

  computed: {
    recentAdjustments() {
      return [...(this.floatAdjustments || [])]
        .sort((a, b) => {
          const aTime = new Date(a.doc.timestamp || 0).getTime();
          const bTime = new Date(b.doc.timestamp || 0).getTime();
          return bTime - aTime;
        })
        .slice(0, 7);
    },
    cashTotal() {
      if (!this.paymentLabels || !this.paymentData) return 0;
      const idx = this.paymentLabels.findIndex(
        (label) => label.toLowerCase() === "cash"
      );
      return idx >= 0 ? Number(this.paymentData[idx] || 0) : 0;
    },
    baseEstimatedFloat() {
      return Number(this.floatAmount || 0);
    },
    estimatedFloatAmount() {
      return this.baseEstimatedFloat + Number(this.pendingAdjustment || 0);
    },
    hasAdjustmentToday() {
      return (this.floatAdjustments || []).some((adj) => this.isToday(adj.doc.timestamp));
    },
    ...mapGetters({
      currency: "currency",
      localSettings: "localSettings",
    }),
  },
};
</script>

<style scoped lang="scss">
@use "@monolith/styles";

.modal {
  .content {
    h1 {
      text-align: center;
      margin-bottom: 2rem;
    }

    .container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
      margin-bottom: 2rem;
    }

    .column-container {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      h5 {
        text-align: center;
        margin-bottom: 1rem;
      }
    }

    .missed-warning {
      background: var(--primary-colour);
      border-radius: 0.5rem;
      padding: 1rem;
      border-left: 3px solid #e67e22;
      box-shadow: var(--box-shadow);
      color: #e67e22;
      font-weight: 600;

      svg {
        margin-right: 0.5rem;
      }
    }

    .info {
      background: var(--primary-colour);
      border-radius: 0.5rem;
      padding: 1rem;
      border-left: 3px solid var(--action-colour);
      box-shadow: var(--box-shadow);

      .blue {
        .cap {
          color: var(--action-colour);
          text-transform: capitalize;
        }

        .today-pill {
          margin-left: 0.5rem;
          display: inline-block;
          padding: 0.05rem 0.5rem;
          border-radius: 999px;
          background: var(--action-colour);
          color: var(--secondary-text-colour);
          font-size: 0.8rem;
          font-weight: 700;
        }
      }
    }

    .enter-name {
      background: var(--tertiary-colour);
      border-radius: 8px;
      padding: 0.5rem 0;

      label {
        display: block;
        margin-bottom: 0.5rem;
      }

      #name {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid var(--border-colour);
        border-radius: 0.5rem;
        background: var(--primary-colour);
        box-sizing: border-box;
        color: var(--text-colour);

        &:focus {
          outline: none;
          border-color: var(--action-colour);
        }
      }
    }

    .row-container {
      background: var(--primary-colour);
      box-shadow: Var(--box-shadow);
      border-radius: 0.5rem;
      padding: 1rem;
      margin: 0;
      list-style: none;

      li {
        .table {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;

          p {
            margin: 0;
          }
        }

        &:last-child .table {
          border-bottom: none;
          padding-top: 1rem;
          margin-top: 0.5rem;
          color: var(--action-colour);
        }
      }
    }

    .estimate {
      background: var(--secondary-colour);
      border-radius: 0.5rem;
      padding: 0.5rem 0;
      text-align: center;
      margin: 0.25rem 0;

      p {
        margin: 0 0 1rem 0;
      }

      .today-adjustment {
        margin: 0 0 0.75rem 0;
        color: var(--action-colour);
        font-weight: 600;

        .adjustment-date {
          margin: 0 0.5rem;
          color: var(--action-colour);
          font-size: 0.85rem;
          font-weight: 500;
          opacity: 0.9;
        }

        .adjustment-reason {
          display: block;
          font-size: 0.85rem;
          font-weight: 400;
          opacity: 0.8;
          margin-top: 0.25rem;
        }
      }

      .adjust-float {
        background: var(--action-colour);
        color: var(--secondary-text-colour);
        border: none;
        width: 100%;
        margin: 0;
        padding: 0.75rem 1rem;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          opacity: 0.9;
        }
      }
    }

    .complete {
      width: 100%;
      background: var(--action-colour);
      color: var(--secondary-text-colour);
      border: none;
      padding: 1rem 1.5rem;
      margin: 0;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: var(--box-shadow);

      &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
      }
    }
  }
}
</style>
